import type { LonLat } from "@atl/contracts";
import { DEFAULT_URL_STATE, serializeUrlState } from "../state/url.ts";

/**
 * A point shared from the Айналма app: `https://ainalma.kz/p?lat=…&lon=…&z=…&n=…` (apps/ios
 * `PlaceLink`, docs in apps/ios/docs/SHARING.md). The page is the fallback when the universal link
 * does not open the app: the point on our map, «Открыть в приложении» and «Маршрут сюда».
 */
export interface SharedPlace {
  point: LonLat;
  zoom: number;
  /** The object name the sender saw; `null` for a plain point or the sender's own position. */
  name: string | null;
}

export const PLACE_PATH = "/p";
export const DEFAULT_PLACE_ZOOM = 17;
export const MAX_PLACE_NAME = 80;
export const APP_STORE_URL = "https://apps.apple.com/kz/app/id6824185711";
export const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=kz.galymzhan.ainalma";

/** Plain decimals only, as in the app: no exponents, hex, `Infinity` or `NaN`. */
const DECIMAL = /^-?(\d+\.?\d*|\.\d+)$/;

function decimal(text: string | null): number | null {
  if (text === null || !DECIMAL.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

const round5 = (value: number): number => Math.round(value * 100_000) / 100_000;

function cleanName(text: string | null): string | null {
  if (text === null) return null;
  const visible = Array.from(text, (ch) => {
    const code = ch.codePointAt(0) ?? 0;
    return code < 0x20 || (code >= 0x7f && code <= 0x9f) ? " " : ch;
  }).join("");
  const collapsed = visible.split(/\s+/).filter(Boolean).join(" ");
  return collapsed.length > 0 ? Array.from(collapsed).slice(0, MAX_PLACE_NAME).join("") : null;
}

export function isPlacePath(pathname: string): boolean {
  return pathname.replace(/\/+$/, "") === PLACE_PATH;
}

/** Parses `location.search` of `/p`; `null` when the point is missing or out of range. */
export function parseSharedPlace(search: string): SharedPlace | null {
  const params = new URLSearchParams(search);
  const lat = decimal(params.get("lat"));
  const lon = decimal(params.get("lon"));
  if (lat === null || lon === null || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  const z = decimal(params.get("z"));
  const zoom = z === null ? DEFAULT_PLACE_ZOOM : Math.round(Math.min(20, Math.max(3, z)) * 10) / 10;
  return { point: { lat: round5(lat), lon: round5(lon) }, zoom, name: cleanName(params.get("n")) };
}

/** The query in the app's exact form: five decimals, the zoom without a trailing `.0`, `%20` for spaces. */
export function placeQuery(place: SharedPlace): string {
  const zoom = Number.isInteger(place.zoom) ? String(place.zoom) : place.zoom.toFixed(1);
  const parts = [
    `lat=${place.point.lat.toFixed(5)}`,
    `lon=${place.point.lon.toFixed(5)}`,
    `z=${zoom}`,
  ];
  if (place.name !== null) parts.push(`n=${encodeURIComponent(place.name)}`);
  return parts.join("&");
}

export function webPlaceLink(place: SharedPlace): string {
  return `https://ainalma.kz${PLACE_PATH}?${placeQuery(place)}`;
}

/** «Открыть в приложении»: a universal link cannot open the app from its own domain, the scheme can. */
export function appPlaceLink(place: SharedPlace): string {
  return `ainalma://p?${placeQuery(place)}`;
}

/** «Маршрут сюда» on this web map: the point becomes Б of the route tab. */
export function routeToPlaceHref(place: SharedPlace): string {
  return `/${serializeUrlState({ ...DEFAULT_URL_STATE, to: { point: place.point, label: place.name } })}`;
}
